import React, { useEffect, useState } from 'react';
import GlobalDialog from '../../../../components/mui/Dialogue/GlobalDialog';
import { AddButton, CancelButton, SubmitButton } from '../../../../components/mui/Buttons/Buttons';
import { useFormik, Form, FormikProvider } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Stack,
  Button,
  Box,
  FormHelperText,
} from '@mui/material';
import {
  getPolicyTypesUnderRoot,
  getRolesUnderDep,
  fileUpload,
  createHrpolicy,
  getAllHrPolicies,
  getWingsUnderDept,
  getRolesUnderWing,
} from '../../../../redux/features/hrPolicySlice';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import useHandleApiResponse from '../../../../Hooks/useHandleApiResponse';
import CreateHrPolicyType from './CreateHrPolicyType';
import * as Yup from 'yup';

const CreatePolicy = ({ typeId }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  const {
    rootPolicies,
    policyTypeUderRoot,
    loading: policyLoading,
    rolesUnderDep,
    wings,
    roles,
  } = useSelector((state) => state.hrPolicy);
  const { users } = useSelector((state) => state.food);
  const { departments } = useSelector((state) => state.permission);
  const handleApiResponse = useHandleApiResponse();

  const Schema = Yup.object().shape({
    rootId: Yup.string().required('rootId is required'),
    hrPolicyTypeId: Yup.string().required('hrPolicyTypeId is required'),
    hrPolicyDocumentPath: Yup.string().required('hrPolicyDocumentPath is required'),
    depId: Yup.string().when('rootId', {
      is: '3',
      then: (schema) => schema.required('depId is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
    mappingId: Yup.string().required('mappingId is required'),
  });

  const formik = useFormik({
    initialValues: {
      rootId: '',
      hrPolicyTypeId: '',
      hrPolicyDocumentPath: '',
      effectiveDate: null,
      expiryDate: null,
      depId: '',
      mappingId: '',
      wingId: '',
    },
    validationSchema: Schema,
    onSubmit: (values) => {
      console.log('Form Submit:', values);
      dispatch(createHrpolicy(values, { resetForm })).then((res) => {
        handleApiResponse(res, {
          onSuccess: () => {
            setOpen(false);
            dispatch(getAllHrPolicies(typeId));
            resetForm();
          },
          onFailed: () => {
            console.log('Something went wrong');
          },
        });
      });
    },
  });
  console.log(formik.values);

  const { values, setFieldValue, resetForm } = formik;

  const uploadFile = async (file, dispatch) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await dispatch(fileUpload(file));

      return response.payload.data.filename;
    } catch (error) {
      console.error('File upload failed:', error);
      return '';
    }
  };

  useEffect(() => {
    if (values.depId) {
      dispatch(getWingsUnderDept(values.depId));
    }
  }, [values.depId]);
  useEffect(() => {
    if (values.wingId) {
      // dispatch(getTheDesignations(values.wingId));
      dispatch(getRolesUnderWing(values.wingId));
    }
  }, [values.wingId]);
  return (
    <GlobalDialog
      title={'Create Policy'}
      open={open}
      setOpen={setOpen}
      closeButton={true}
      fullScreen
      popupButton={
        <AddButton action={() => setOpen(true)} size={'small'}>
          Add Policy
        </AddButton>
      }
      content={
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <CreateHrPolicyType />
          </Box>
          <FormikProvider value={formik}>
            <Form onSubmit={formik.handleSubmit}>
              <Stack spacing={3}>
                {/* Root Policy Type Select */}
                <FormControl fullWidth error={formik.touched.rootId && Boolean(formik.errors.rootId)}>
                  <InputLabel id="root-select-label">Root Policy Type</InputLabel>
                  <Select
                    labelId="root-select-label"
                    name="rootId"
                    label="Root Policy Type"
                    value={formik.values.rootId}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value == 1) {
                        formik.setFieldValue('mappingId', 0);
                      } else {
                        formik.setFieldValue('mappingId', '');
                      }
                      formik.setFieldValue('rootId', value);
                      formik.setFieldValue('hrPolicyTypeId', '');
                      formik.setFieldValue('hrPolicyDocumentPath', '');
                      formik.setFieldValue('depId', '');

                      dispatch(getPolicyTypesUnderRoot(value));
                    }}
                  >
                    {rootPolicies.map((item) => (
                      <MenuItem key={item.root_policy_type_id} value={item.root_policy_type_id}>
                        {item.root_policy_type_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {formik.touched.rootId && formik.errors.rootId && (
                    <FormHelperText>{formik.errors.rootId}</FormHelperText>
                  )}
                </FormControl>

                {/* HR Policy Type Autocomplete */}
                <Autocomplete
                  fullWidth
                  options={policyTypeUderRoot || []}
                  getOptionLabel={(option) => option.hr_policy_type_name || ''}
                  value={
                    policyTypeUderRoot.find((item) => item.hr_policy_type_id === formik.values.hrPolicyTypeId) || null
                  }
                  onChange={(e, newValue) => {
                    formik.setFieldValue('hrPolicyTypeId', newValue ? newValue.hr_policy_type_id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="HR Policy Type"
                      placeholder="Select HR Policy Type"
                      error={formik.touched.hrPolicyTypeId && Boolean(formik.errors.hrPolicyTypeId)}
                      helperText={formik.errors.hrPolicyTypeId}
                    />
                  )}
                />

                {/* File Upload (PDF only) */}
                <Stack spacing={1}>
                  <FormControl>
                    <Button disabled={policyLoading} variant="outlined" component="label" color="primary">
                      {policyLoading ? 'Please wait file uploading...' : 'Upload Policy Document (PDF max - 3MB)'}

                      <input
                        type="file"
                        hidden
                        accept="application/pdf"
                        onChange={async (e) => {
                          const file = e.currentTarget.files?.[0];
                          if (!file) return;

                          const MAX_SIZE = 3 * 1024 * 1024; // 3MB

                          // ✅ Size validation
                          if (file.size > MAX_SIZE) {
                            alert('File size should not exceed 3MB');
                            e.target.value = ''; // reset input
                            return;
                          }

                          // (Optional) type validation safety
                          if (file.type !== 'application/pdf') {
                            alert('Only PDF files are allowed');
                            e.target.value = '';
                            return;
                          }

                          const fileKey = await uploadFile(file, dispatch);
                          if (fileKey) {
                            setFieldValue('hrPolicyDocumentPath', fileKey);
                          }
                        }}
                      />
                    </Button>

                    {formik.touched.hrPolicyDocumentPath && formik.errors.hrPolicyDocumentPath && (
                      <FormHelperText>{formik.errors.hrPolicyDocumentPath}</FormHelperText>
                    )}
                  </FormControl>
                  {values.hrPolicyDocumentPath && (
                    <TextField label="Uploaded File" value={values.hrPolicyDocumentPath} fullWidth disabled />
                  )}
                </Stack>
                {values.hrPolicyDocumentPath && (
                  <iframe
                    height={300}
                    width={'100%'}
                    src={`${process.env.REACT_APP_UPLOADS_URL}${values.hrPolicyDocumentPath}`}
                  />
                )}

                {/* Users */}
                {values.rootId && values.rootId == 4 ? (
                  <Autocomplete
                    fullWidth
                    options={users || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={users.find((item) => item.userID === formik.values.mappingId) || null}
                    onChange={(e, newValue) => {
                      formik.setFieldValue('mappingId', newValue ? newValue.userID : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Users"
                        placeholder="Select user"
                        error={formik.touched.mappingId && Boolean(formik.errors.mappingId)}
                        helperText={formik.errors.mappingId}
                      />
                    )}
                  />
                ) : null}

                {/* Department */}
                {(values.rootId && values.rootId == 2) || values.rootId == 3 ? (
                  <Autocomplete
                    fullWidth
                    options={departments || []}
                    getOptionLabel={(option) => option.dept_name || ''}
                    value={
                      departments.find((item) =>
                        values.rootId == 3 ? item.dept_id === values.depId : item.dept_id === values.mappingId
                      ) || null
                    }
                    onChange={(e, newValue) => {
                      if (values.rootId == 3) {
                        dispatch(getRolesUnderDep(newValue.dept_id));
                        formik.setFieldValue('depId', newValue ? newValue.dept_id : '');
                        formik.setFieldValue('mappingId', '');
                      } else {
                        formik.setFieldValue('mappingId', newValue ? newValue.dept_id : '');
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Department"
                        placeholder="Select department"
                        error={formik.touched.depId && Boolean(formik.errors.depId)}
                        helperText={formik.errors.depId}
                      />
                    )}
                  />
                ) : null}

                {/* ------------------------------------------------- */}
                {values.depId && values.rootId == 3 ? (
                  <Autocomplete
                    fullWidth
                    options={wings || []}
                    getOptionLabel={(option) => option.wing_name || ''}
                    value={wings.find((item) => item.wing_id === formik.values.wingId) || null}
                    onChange={(e, newValue) => {
                      formik.setFieldValue('wingId', newValue ? newValue.wing_id : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Wings"
                        placeholder="Select Wing"
                        error={formik.touched.wingId && Boolean(formik.errors.wingId)}
                        helperText={formik.errors.wingId}
                      />
                    )}
                  />
                ) : null}

                {/* Roles */}
                {values.wingId && values.rootId == 3 ? (
                  <Autocomplete
                    fullWidth
                    options={roles || []}
                    getOptionLabel={(option) => option.usertype || ''}
                    value={roles.find((item) => item.usertypeID === formik.values.mappingId) || null}
                    onChange={(e, newValue) => {
                      formik.setFieldValue('mappingId', newValue ? newValue.usertypeID : '');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Roles"
                        placeholder="Select role"
                        error={formik.touched.mappingId && Boolean(formik.errors.mappingId)}
                        helperText={formik.errors.mappingId}
                      />
                    )}
                  />
                ) : null}

                {/* Date Pickers */}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Effective Date"
                    value={formik.values.effectiveDate ? dayjs(formik.values.effectiveDate) : null}
                    onChange={(newValue) => formik.setFieldValue('effectiveDate', newValue)}
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />

                  <DatePicker
                    label="Expiry Date"
                    value={formik.values.expiryDate ? dayjs(formik.values.expiryDate) : null}
                    onChange={(newValue) => formik.setFieldValue('expiryDate', newValue)}
                    slotProps={{
                      textField: { fullWidth: true },
                    }}
                  />
                </LocalizationProvider>
              </Stack>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Stack direction={'row'} spacing={2}>
                  <CancelButton action={() => setOpen(false)}>Cancel</CancelButton>
                  <SubmitButton disabled={policyLoading} type={'submit'}>
                    {policyLoading ? 'please wait' : 'Submit'}
                  </SubmitButton>
                </Stack>
              </Box>
            </Form>
          </FormikProvider>
        </>
      }
    />
  );
};

export default CreatePolicy;
